import { IncDocument } from "@core/domain/models/inc-document.model";
import { Nullable } from "@core/domain/types/nullable.type";
import { Undefinable } from "@core/domain/types/undefinable.type";
import { DateTime } from "luxon";
import { ProjectActivityTask } from "./project-activity-task.model";

export interface TechnicalProposalsFilters {
    projectId?: Undefinable<number>;
}

export interface ActivitySlot {
    name: string;
    value: string;
}

export interface IndicatorTaskSummary {
    id: number;
    description: string;
}

export interface IndicatorGenderBreakdown {
    women?: Undefinable<Nullable<string>>;
    men?: Undefinable<Nullable<string>>;
    nonBinary?: Undefinable<Nullable<string>>;
    unspecified?: Undefinable<Nullable<string>>;
}

export interface IndicatorTrafficLightTargetConfig {
    greenLabel?: Undefinable<string>;
    yellowLabel?: Undefinable<string>;
    redLabel?: Undefinable<string>;
}

export interface IndicatorCategoryTargetConfig {
    options?: Undefinable<string[]>;
}

export interface IndicatorTargetConfig {
    trafficLight?: Undefinable<IndicatorTrafficLightTargetConfig>;
    category?: Undefinable<IndicatorCategoryTargetConfig>;
}

export enum IndicatorCommentTypeEnum {
    note = "note",
    incident = "incident",
    periodClose = "period_close",
    funderReview = "funder_review",
}

export class IndicatorComment {
    constructor(
        public readonly id: number,
        public readonly indicatorId: number,
        public text: string,
        public commentType: IndicatorCommentTypeEnum,
        public createdAt: DateTime,
        public modifiedAt: DateTime,
        public createdByName?: Undefinable<Nullable<string>>,
        public referenceDate?: Undefinable<Nullable<DateTime>>,
        public commentTypeLabel?: Undefinable<string>,
    ) {}
}

export enum IndicatorFrequencyEnum {
    weekly = "weekly",
    monthly = "monthly",
    quarterly = "quarterly",
    semiannual = "semiannual",
    other = "other",
}

export enum IndicatorTypeEnum {
    quantitative = "quantitative",
    qualitative = "qualitative",
}

export enum IndicatorPurposeTypologyEnum {
    tracking = "tracking",
    evaluation = "evaluation",
    impact = "impact",
    gender = "gender",
}

export enum IndicatorTargetTypeEnum {
    absolute = "absolute",
    percentage = "percentage",
    scale = "scale",
    trafficLight = "traffic_light",
    category = "category",
}

export enum IndicatorDerivedStatusCodeEnum {
    onTrack = "on_track",
    atRisk = "at_risk",
    offTrack = "off_track",
}

export class Indicator {
    constructor(
        public readonly id: number,
        public activityId: number,
        public name: string,
        public description: string,
        public calculationCriteria: string,
        public estimatedIndicator: string,
        public achievedIndicator: Nullable<string>,
        public verificationFont: string,
        public frequency: IndicatorFrequencyEnum = IndicatorFrequencyEnum.monthly,
        public indicatorType: IndicatorTypeEnum = IndicatorTypeEnum.quantitative,
        public targetType: IndicatorTargetTypeEnum = IndicatorTargetTypeEnum.absolute,
        public purposeTypology: Nullable<IndicatorPurposeTypologyEnum> = null,
        public responsibleEmployeeId?: Undefinable<Nullable<number>>,
        public status?: Undefinable<string>,
        public activityName?: Undefinable<string>,
        public disaggregateByGender: boolean = false,
        public estimatedGenderBreakdown: IndicatorGenderBreakdown = {},
        public achievedGenderBreakdown: IndicatorGenderBreakdown = {},
        public frequencyLabel?: Undefinable<string>,
        public indicatorTypeLabel?: Undefinable<string>,
        public purposeTypologyLabel?: Undefinable<string>,
        public targetTypeLabel?: Undefinable<string>,
        public responsibleEmployeeName?: Undefinable<Nullable<string>>,
        public latestCommentText?: Undefinable<Nullable<string>>,
        public commentCount?: Undefinable<number>,
        public lastMonitoringDate?: Undefinable<Nullable<DateTime>>,
        public nextMonitoringDueDate?: Undefinable<Nullable<DateTime>>,
        public monitoringUpdateOverdue?: Undefinable<boolean>,
        public completionPercentage?: Undefinable<Nullable<number>>,
        public derivedStatus?: Undefinable<Nullable<string>>,
        public derivedStatusCode?: Undefinable<
            Nullable<IndicatorDerivedStatusCodeEnum>
        >,
        public targetConfig?: Undefinable<IndicatorTargetConfig>,
        public slots?: Undefinable<ActivitySlot[]>,
        public tasks?: Undefinable<IndicatorTaskSummary[]>,
    ) {}
}

export class CreateIndicatorComment {
    constructor(
        public readonly text: string,
        public readonly commentType: IndicatorCommentTypeEnum,
        public readonly referenceDate?: Undefinable<Nullable<DateTime>>,
    ) {}
}

export enum ActivityStatusEnum {
    toDo = "toDo",
    inProgress = "inProgress",
    blocked = "blocked",
    done = "done",
}

export enum ActivityPriorityEnum {
    low = "low",
    medium = "medium",
    high = "high",
}

export interface KanbanBlockData {
    id: number;
    name: string;
    position: number;
    phase: string;
    project: number;
}

export class Activity {
    // eslint-disable-next-line max-params
    constructor(
        public readonly id: number,
        public name: string,
        public description: string,
        public needs: string,
        public objective: number,
        public estimatedExecutionStartDate?: Undefinable<DateTime>,
        public estimatedExecutionEndDate?: Undefinable<DateTime>,
        public executionStartDate?: Undefinable<DateTime>,
        public executionEndDate?: Undefinable<DateTime>,
        public employeeIds: number[] = [],
        public dependencyIds: number[] = [],
        public indicators: Indicator[] = [],
        public status: ActivityStatusEnum = ActivityStatusEnum.toDo,
        public assignedEmployee?: Undefinable<number>,
        public technicalProposalId?: Undefinable<number>,
        public deadline?: DateTime,
        public comment?: Undefinable<string>,
        public tasks: ProjectActivityTask[] = [],
        public materialIds: number[] = [],
        public priority: ActivityPriorityEnum = ActivityPriorityEnum.medium,
        public blockId?: Undefinable<Nullable<number>>,
        public blockData?: Undefinable<Nullable<KanbanBlockData>>,
    ) {}
}

export class Objective {
    constructor(
        public readonly id: number,
        public name: string,
        public description: string,
        public technicalProposalId: number,
        public activities: Activity[] = [],
    ) {}
}

export class TechnicalProposal {
    constructor(
        public readonly id: number,
        public readonly projectId: number,
        public overallObjective: string,
        public objectives: Objective[] = [],
        public documents: IncDocument[] = [],
    ) {}
}

export class TechnicalProposals {
    constructor(
        public readonly technicalProposals: TechnicalProposal[],
        public readonly total: number,
    ) {}
}

export class CreateIndicator {
    constructor(
        public readonly activityId: Indicator["activityId"],
        public readonly name: Indicator["name"],
        public readonly description: Indicator["description"],
        public readonly calculationCriteria: Indicator["calculationCriteria"],
        public readonly estimatedIndicator: Indicator["estimatedIndicator"],
        public readonly achievedIndicator: Indicator["achievedIndicator"],
        public readonly verificationFont: Indicator["verificationFont"],
        public readonly frequency: Indicator["frequency"],
        public readonly indicatorType: Indicator["indicatorType"],
        public readonly targetType: Indicator["targetType"],
        public readonly purposeTypology: Nullable<IndicatorPurposeTypologyEnum> = null,
        public readonly disaggregateByGender: Indicator["disaggregateByGender"],
        public readonly estimatedGenderBreakdown: Indicator["estimatedGenderBreakdown"],
        public readonly achievedGenderBreakdown: Indicator["achievedGenderBreakdown"],
        public readonly targetConfig?: Indicator["targetConfig"],
        public readonly responsibleEmployeeId?: Indicator["responsibleEmployeeId"],
        public readonly status?: Indicator["status"],
    ) {}
}

export class EditIndicator extends CreateIndicator {
    constructor(
        public readonly id: Indicator["id"],
        activityId: CreateIndicator["activityId"],
        name: CreateIndicator["name"],
        description: CreateIndicator["description"],
        calculationCriteria: CreateIndicator["calculationCriteria"],
        estimatedIndicator: CreateIndicator["estimatedIndicator"],
        achievedIndicator: CreateIndicator["achievedIndicator"],
        verificationFont: CreateIndicator["verificationFont"],
        frequency: CreateIndicator["frequency"],
        indicatorType: CreateIndicator["indicatorType"],
        targetType: CreateIndicator["targetType"],
        purposeTypology: Nullable<IndicatorPurposeTypologyEnum> = null,
        disaggregateByGender: CreateIndicator["disaggregateByGender"],
        estimatedGenderBreakdown: CreateIndicator["estimatedGenderBreakdown"],
        achievedGenderBreakdown: CreateIndicator["achievedGenderBreakdown"],
        targetConfig?: CreateIndicator["targetConfig"],
        responsibleEmployeeId?: CreateIndicator["responsibleEmployeeId"],
        status?: CreateIndicator["status"],
        public slots?: Indicator["slots"],
    ) {
        super(
            activityId,
            name,
            description,
            calculationCriteria,
            estimatedIndicator,
            achievedIndicator,
            verificationFont,
            frequency,
            indicatorType,
            targetType,
            purposeTypology,
            disaggregateByGender,
            estimatedGenderBreakdown,
            achievedGenderBreakdown,
            targetConfig,
            responsibleEmployeeId,
            status,
        );
    }
}

export class CreateActivity {
    // eslint-disable-next-line max-params
    constructor(
        public readonly objectiveId: Activity["objective"],
        public readonly name: Activity["name"],
        public readonly description: Activity["description"],
        public readonly needs: Activity["needs"],
        public readonly estimatedExecutionStartDate?: Activity["estimatedExecutionStartDate"],
        public readonly estimatedExecutionEndDate?: Activity["estimatedExecutionEndDate"],
        public readonly executionStartDate?: Activity["executionStartDate"],
        public readonly executionEndDate?: Activity["executionEndDate"],
        public readonly employeeIds?: Activity["employeeIds"],
        public readonly materialIds?: Activity["materialIds"],
        public readonly indicators: CreateIndicator[] = [],
    ) {}
}

export class EditActivity extends CreateActivity {
    // eslint-disable-next-line max-params
    constructor(
        public readonly id: Activity["id"],
        objectiveId: Activity["objective"],
        name: Activity["name"],
        description: Activity["description"],
        needs: Activity["needs"],
        estimatedExecutionStartDate?: Activity["estimatedExecutionStartDate"],
        estimatedExecutionEndDate?: Activity["estimatedExecutionEndDate"],
        executionStartDate?: Activity["executionStartDate"],
        executionEndDate?: Activity["executionEndDate"],
        employeeIds?: Activity["employeeIds"],
        materialIds?: Activity["materialIds"],
        public override indicators: (CreateIndicator | EditIndicator)[] = [],
    ) {
        super(
            objectiveId,
            name,
            description,
            needs,
            estimatedExecutionStartDate,
            estimatedExecutionEndDate,
            executionStartDate,
            executionEndDate,
            employeeIds,
            materialIds,
            indicators,
        );
    }
}

export class CreateObjective {
    constructor(
        public readonly name: Objective["name"],
        public readonly description: Objective["description"],
        public readonly technicalProposalId: Objective["technicalProposalId"],
    ) {}
}

export class EditObjective extends CreateObjective {
    constructor(
        public readonly id: Objective["id"],
        name: Objective["name"],
        description: Objective["description"],
        technicalProposalId: Objective["technicalProposalId"],
    ) {
        super(name, description, technicalProposalId);
    }
}

export class CreateTechnicalProposal {
    constructor(
        public readonly overallObjective: TechnicalProposal["overallObjective"],
        public readonly projectId: TechnicalProposal["projectId"],
        public readonly documents?: Undefinable<IncDocument[]>,
    ) {}
}

export class EditTechnicalProposal extends CreateTechnicalProposal {
    constructor(
        public readonly id: TechnicalProposal["id"],
        project: TechnicalProposal["projectId"],
        overallObjective: TechnicalProposal["overallObjective"],
        documents?: TechnicalProposal["documents"],
    ) {
        super(overallObjective, project, documents);
    }
}
